import moment from 'moment';
import qs from 'query-string';

import { formatDateForSearchUrl } from './url';
const DEPART_DIRECTION = 'depart';
const RETURN_DIRECTION = 'return';

function directionIsValidOnServer(direction, departTripId) {
    if (direction === DEPART_DIRECTION) {
        return true;
    }

    return !!departTripId;
}

function directionIsValid(direction, returnDate) {
    if (direction === RETURN_DIRECTION) {
        return !!returnDate;
    }

    return direction === DEPART_DIRECTION;
}

function _departDateIsValid(departDate) {
    if (!departDate) {
        return false;
    }
    departDate = moment(departDate);
    const today = moment();

    return departDate.isValid() && departDate.isSameOrAfter(today, 'day');
}

function _datePairIsValid(departDate, returnDate) {
    if (!returnDate) {
        return true;
    }

    departDate = moment(departDate);
    returnDate = moment(returnDate);

    return departDate.isValid()
        && returnDate.isValid()
        && departDate.isSameOrBefore(returnDate, 'day');
}

function urlIsValidOnServer(params) {
    if (!directionIsValidOnServer(params.direction, params.departTripId)) {
        return false;
    }

    return urlIsValid(params);
}

function urlIsValid(params) {
    let {
        direction,
        departDate,
        returnDate,
        originText,
        originPlaceId,
        destinationText,
        destinationPlaceId,
        urlDestinationText,
        urlOriginText,
        urlOriginPlaceId,
        urlDestinationPlaceId,
    } = params;

    urlOriginText = decodeURIComponent(urlOriginText);
    originText = originText
        ? decodeURIComponent(originText)
        : undefined;
    urlDestinationText = decodeURIComponent(urlDestinationText);
    destinationText = destinationText
        ? decodeURIComponent(destinationText)
        : undefined;

    if (destinationText && originText) {
        const originMatches = urlOriginText === originText;
        const destinationMatches = urlDestinationText === destinationText;
        if (!originMatches || !destinationMatches) {
            return false;
        }
    }

    if (originPlaceId && destinationPlaceId) {
        const originMatches = urlOriginPlaceId === originPlaceId;
        const destinationMatches = urlDestinationPlaceId === destinationPlaceId;
        if (!originMatches || !destinationMatches) {
            return false;
        }
    }

    if (!directionIsValid(direction, returnDate)) {
        return false;
    }

    return _departDateIsValid(departDate) && _datePairIsValid(departDate, returnDate);
}

function buildUrlParams(urlSearchParams, searchParams = {}) {
    return {
        direction: urlSearchParams.direction,
        departDate: urlSearchParams.departDate,
        returnDate: urlSearchParams.returnDate,
        originText: searchParams.originText || urlSearchParams.originText,
        passengers: searchParams.passengers || urlSearchParams.passengers,
        destinationText: searchParams.destinationText || urlSearchParams.destinationText,
        originPlaceId: searchParams.originPlaceId || urlSearchParams.originPlaceId,
        destinationPlaceId: searchParams.destinationPlaceId || urlSearchParams.destinationPlaceId,
        departTripId: searchParams.departTripId || urlSearchParams.departTripId,
        urlOriginText: urlSearchParams.originText,
        urlOriginPlaceId: urlSearchParams.originPlaceId,
        urlPassengers: urlSearchParams.passengers,
        urlDestinationText: urlSearchParams.destinationText,
        urlDestinationPlaceId: urlSearchParams.destinationPlaceId,
        urlDepartTripId: urlSearchParams.departTripId,
    };
}

function buildValidServerUrl(params) {
    if (params.direction === RETURN_DIRECTION) {
        params.direction = DEPART_DIRECTION;
    }

    return buildValidUrl(params);
}

function buildValidUrl(params, urlFilterParams = {}) {
    const {
        direction,
        originText,
        originPlaceId,
        destinationText,
        destinationPlaceId,
        departDate,
        returnDate,
        departTripId,
        passengers,
    } = params;

    const invalidOrigin = !originText && !originPlaceId;
    const invalidDestination = !destinationText && !destinationPlaceId;
    if (invalidOrigin || invalidDestination) {
        return;
    }

    const directionToSet = directionIsValid(direction, returnDate)
        ? direction
        : DEPART_DIRECTION;
    const today = moment().format('YYYY-MM-DD');
    const validDepartDate = _departDateIsValid(departDate)
        ? departDate
        : today;
    const validReturnDate = _datePairIsValid(validDepartDate, returnDate)
        ? returnDate
        : validDepartDate;

    const urlComps = [
        directionToSet,
        originText,
        destinationText,
        validDepartDate,
        validReturnDate,
    ];
    const fullPath = urlComps.reduce((path, urlComp) => {
        if (!urlComp) {
            return path;
        }
        return `${path}/${encodeURIComponent(urlComp)}`;
    }, '');

    let queries = {
        opid: originPlaceId,
        dpid: destinationPlaceId,
        p: passengers,
        ...urlFilterParams,
    };

    if (direction === RETURN_DIRECTION) {
        queries.dtid = departTripId;
    }

    return `${fullPath}/?${qs.stringify(queries)}`;
}

function getItineraryId(outboundTripIds, returnTripIds = []) {
    let itineraryId = outboundTripIds.join('|');
    if (returnTripIds.length) {
        itineraryId = itineraryId.concat('||').concat(returnTripIds.join('|'));
    }
    return itineraryId;
}

function buildSearchUrl(opts, urlFilterParams) {
    const {
        direction,
        departDate,
        returnDate,
        originText,
        destinationText,
        originPlaceId,
        destinationPlaceId,
        departTripId,
        passengers,
    } = opts;

    let urlParams = null;

    const params = {
        direction,
        departDate: formatDateForSearchUrl(departDate),
        returnDate: returnDate
            ? formatDateForSearchUrl(returnDate)
            : null,
        originText,
        destinationText,
        originPlaceId,
        passengers,
        destinationPlaceId,
        departTripId,
    };

    urlParams = buildUrlParams(params);

    return buildValidUrl(urlParams, urlFilterParams);
}

export {
    buildSearchUrl,
    buildUrlParams,
    buildValidServerUrl,
    buildValidUrl,
    directionIsValid,
    directionIsValidOnServer,
    getItineraryId,
    urlIsValid,
    urlIsValidOnServer,
};
